import { builders } from '../data/builders';
import { curatedModels } from '../data/models';
import { computeProviders, modelPlatforms } from '../data/stack';
import { labCountry, platformCountry, regionalPlatforms } from '../data/geography';
import { Builder } from '../types/builders';
import { CuratedModel } from '../types/content';

export interface EcoPlatform {
  name: string;
  kind: string;
  url: string;
}

export function modelCountry(m: CuratedModel): string | null {
  return labCountry[m.lab] ?? null;
}

export function buildersIn(cca3: string): Builder[] {
  return builders.filter((b) => b.cca3 === cca3);
}

export function modelsFrom(cca3: string): CuratedModel[] {
  return curatedModels.filter((m) => modelCountry(m) === cca3);
}

export function modelPlatformsFrom(cca3: string): EcoPlatform[] {
  const stack = modelPlatforms.filter((p) => platformCountry[p.name] === cca3).map((p) => ({ name: p.name, kind: p.type, url: p.sourceUrl }));
  const regional = regionalPlatforms.filter((p) => p.cca3 === cca3 && p.kind !== 'Compute').map((p) => ({ name: p.name, kind: p.kind, url: p.url }));
  return [...stack, ...regional];
}

export function computeFrom(cca3: string): EcoPlatform[] {
  const stack = computeProviders.filter((c) => platformCountry[c.name] === cca3).map((c) => ({ name: c.name, kind: c.kind, url: c.sourceUrl }));
  const regional = regionalPlatforms.filter((p) => p.cca3 === cca3 && p.kind === 'Compute').map((p) => ({ name: p.name, kind: 'Compute', url: p.url }));
  return [...stack, ...regional];
}

export function platformsFrom(cca3: string): EcoPlatform[] {
  return [...modelPlatformsFrom(cca3), ...computeFrom(cca3)];
}

function countBy<T>(items: T[], key: (t: T) => string | null): Record<string, number> {
  const out: Record<string, number> = {};
  items.forEach((t) => {
    const k = key(t);
    if (k) out[k] = (out[k] ?? 0) + 1;
  });
  return out;
}

export const builderCounts = countBy(builders, (b) => b.cca3);
export const modelCounts = countBy(curatedModels, modelCountry);
export const openWeightCounts = countBy(
  curatedModels.filter((m) => m.license !== 'proprietary'),
  modelCountry,
);

export interface CountryEcoRow {
  cca3: string;
  name: string;
  cca2: string | null;
  models: number;
  openWeight: number;
  builders: number;
  platforms: number;
  compute: number;
}

export function ecosystemPivot(names: Record<string, { name: string; cca2: string | null }>): CountryEcoRow[] {
  const codes = new Set<string>([
    ...Object.keys(builderCounts),
    ...Object.keys(modelCounts),
    ...Object.values(platformCountry),
    ...regionalPlatforms.map((p) => p.cca3),
  ]);
  return Array.from(codes).map((cca3) => ({
    cca3,
    name: names[cca3]?.name ?? cca3,
    cca2: names[cca3]?.cca2 ?? null,
    models: modelCounts[cca3] ?? 0,
    openWeight: openWeightCounts[cca3] ?? 0,
    builders: builderCounts[cca3] ?? 0,
    platforms: modelPlatformsFrom(cca3).length,
    compute: computeFrom(cca3).length,
  }));
}
